import { v4 as uuid } from 'uuid';
import CoreBase from '../../../services/engines/CoreBase';
import UmlConnector from '../../Types/UmlConnector';
import { flattenClassShapes } from './CanvasShapeFlattener';
import dependencyResolver from './DependencyResolver';
import outputMsgEngine from '../../../Widgets/OutputMsgWidget/Core/OutputMsgEngine';

/**
 * Builds inheritance/realization connectors between shapes — generic core on
 * flat, language-agnostic entries {id, name, inherits}, plus a retroactive
 * whole-canvas variant. Works for class-extends-class, class-implements-
 * interface, and interface-extends-interface alike, since C# uses the same
 * base-list syntax for all three and CsTypeScanner doesn't distinguish them
 * in `inherits[]`.
 */
class InheritanceConnectorBuilder extends CoreBase {
    _name = 'InheritanceConnectorBuilder';

    /**
     * Builds one 'inheritance' connector per (entry, baseName) pair where
     * baseName resolves (by exact name match) to another entry in the SAME
     * list. Unresolved base names (BCL types, or a base outside the given
     * list) are silently skipped. De-duped against BOTH existingConnectors
     * and connectors already queued within this call.
     * @param {{id: string, name: string, inherits: string[]}[]} entries
     * @param {import('../../Types/UmlConnector').default[]} existingConnectors
     * @returns {import('../../Types/UmlConnector').default[]}
     */
    buildInheritanceConnectors = (entries, existingConnectors) => {
        const nameToId = new Map();
        entries.forEach((entry) => {
            if (entry.name && !nameToId.has(entry.name)) {
                nameToId.set(entry.name, entry.id);
            }
        });

        const seenPairs = new Set(
            existingConnectors.map((c) => `${c.fromShapeId}->${c.toShapeId}`),
        );

        const connectors = [];
        const bases = entries.flatMap((entry) =>
            (entry.inherits || []).map((baseName) => ({ fromId: entry.id, baseName })),
        );

        bases.forEach(({ fromId, baseName }) => {
            const toId = nameToId.get(baseName);
            if (toId === fromId) return; // self-reference, skip silently
            if (!toId) {
                if (dependencyResolver.isLikelyUserType(baseName)) {
                    outputMsgEngine.addMsg('UmlWorkspace', 'warning', `Base type not found: ${baseName}`);
                }
                return;
            }

            const pairKey = `${fromId}->${toId}`;
            if (seenPairs.has(pairKey)) return;

            seenPairs.add(pairKey);
            connectors.push(new UmlConnector(uuid(), fromId, toId, 'inheritance'));
        });

        return connectors;
    };

    /**
     * Retroactive, whole-canvas pass — covers classes/interfaces that were
     * imported in separate, unrelated pastes and never got an inheritance
     * connector at import time. Reaches into package children too (via
     * flattenClassShapes).
     * @param {import('../../Types/UmlShape').default[]} shapes - umlCanvasEngine.shapes (top-level)
     * @param {import('../../Types/UmlConnector').default[]} existingConnectors - umlCanvasEngine.connectors
     * @returns {import('../../Types/UmlConnector').default[]}
     */
    buildCanvasInheritanceConnectors = (shapes, existingConnectors) => {
        const entries = flattenClassShapes(shapes).map((shape) => ({
            id: shape.id,
            name: shape.caption?.text,
            inherits: shape.inherits,
        }));

        return this.buildInheritanceConnectors(entries, existingConnectors);
    };
}

const inheritanceConnectorBuilder = new InheritanceConnectorBuilder();
export default inheritanceConnectorBuilder;
